import { type AccountInfo } from '@azure/msal-common'
import { useMsal } from '@azure/msal-react'
import { FormControl, ListSubheader, MenuItem, Select, type SelectChangeEvent } from '@mui/material'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from '../../app/hooks'
import logger from '../../app/middleware/log'
import { switchUserContext } from '../api'
import { fetchLoggedInUserThunk } from '../slice'
import { fetchPersonas, type Persona } from './personaApi'
import messages from './personaMessages'
import { getActivePersonaUserId, setActivePersonaUserId } from './personaStorage'

// E8 F8-5 (stories 8.5.1/8.5.2): the in-header persona switcher for the real-auth shell. Lists every
// persona the caller has (GET /auth/personas — the same source the persona splash uses, the only one that
// carries each persona's User id), split into:
//   - "this workspace": personas on the current subdomain -> switched in-session.
//   - "other workspaces": personas on a different subdomain -> reached by a full navigation to that host's
//     /persona-splash?personaId=, which PersonaGate re-auto-selects (and the splash route redirects on).
//
// A same-workspace switch writes the persona key (sent as X-LogikSpark-Persona-Id; /user/me is header-driven
// post-E9) AND best-effort calls switch-context: E9 was USERS-only activation, so the other hosts still
// resolve the persona from the External-ID activeUserId extension that switch-context updates. Both stay
// until the estate-wide cutover. Renders nothing when the caller has 0/1 persona (nothing to switch).

const personaValue = (p: Persona): string => String(p.userId)

const personaLabel = (p: Persona, accountOwner: string): string => {
  const base = p.customerDisplayName
    ? `${p.tenantDisplayName} — ${p.customerDisplayName}`
    : p.tenantDisplayName
  return p.isAccountOwner ? `${base} (${accountOwner})` : base
}

const currentSubdomain = (): string => window.location.hostname.split('.')[0].toLowerCase()

// Everything after the leading label, e.g. acme.dev.logikspark.ai -> dev.logikspark.ai. Empty for a
// single-label host (localhost), where cross-subdomain switching does not apply.
const domainSuffix = (): string => window.location.hostname.split('.').slice(1).join('.')

const PersonaSwitcher = () => {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const { instance } = useMsal()
  // homeAccountId is a stable string; getActiveAccount() itself returns a fresh object every call, so
  // keying the effect on the object would re-fire it every render (-> setPersonas -> render -> refetch loop).
  const accountId: string | undefined = instance.getActiveAccount()?.homeAccountId

  const [personas, setPersonas] = useState<Persona[]>([])
  const [selected, setSelected] = useState<string>(() => {
    const id = getActivePersonaUserId()
    return id ? String(id) : ''
  })

  useEffect(() => {
    const account: AccountInfo | null = instance.getActiveAccount()
    if (!account) {
      return
    }
    let cancelled = false
    fetchPersonas(instance, account)
      .then((list) => {
        if (!cancelled) {
          setPersonas(list)
        }
      })
      .catch((error) => {
        // Non-critical: the switcher just stays hidden. The auth-critical persona load is PersonaGate's job.
        logger.error('PersonaSwitcher: could not load personas (switcher hidden):', error)
      })
    return () => {
      cancelled = true
    }
  }, [instance, accountId])

  // If the persisted active persona is no longer among the caller's personas (revoked mid-session, or a
  // stale key from before this list loaded), drop the selection so MUI's Select doesn't warn about an
  // out-of-range value and the control renders clean. The user re-picks a valid persona normally.
  useEffect(() => {
    if (selected && !personas.some((p) => personaValue(p) === selected)) {
      setSelected('')
    }
  }, [personas, selected])

  // Nothing to switch between -> no switcher.
  if (personas.length <= 1) {
    return null
  }

  const sub = currentSubdomain()
  const here = personas.filter((p) => p.tenantSubdomain.toLowerCase() === sub)
  const elsewhere = personas.filter((p) => p.tenantSubdomain.toLowerCase() !== sub)

  const handleChange = async (event: SelectChangeEvent) => {
    const target = personas.find((p) => personaValue(p) === event.target.value)
    if (!target) {
      return
    }

    // Cross-subdomain: re-bootstrap the SPA under the destination host. PersonaGate re-auto-selects from
    // ?personaId there, and /persona-splash redirects on into the app once resolved.
    if (target.tenantSubdomain.toLowerCase() !== sub) {
      const suffix = domainSuffix()
      if (!suffix) {
        logger.error('PersonaSwitcher: no domain suffix for cross-subdomain switch; ignoring')
        return
      }
      window.location.href = `https://${target.tenantSubdomain}.${suffix}/persona-splash?personaId=${target.userId}`
      return
    }

    // Same workspace: switch in-session. Set the persona key first so the /user/me refetch resolves the new
    // persona via the header. The dropdown update is optimistic: if the refetch fails, the header (which is
    // authoritative) is already on the chosen persona so routing stays correct — only the visible name/branding
    // lags until the next successful fetch. Rolling back the dropdown would need a success signal from the
    // thunk (it currently swallows its own error); deferred as a low-value follow-up.
    setSelected(event.target.value)
    setActivePersonaUserId(target.userId)
    try {
      await switchUserContext(target.tenantId, target.customerId ?? undefined)
    } catch (error) {
      logger.error('PersonaSwitcher: switch-context failed (continuing; /user/me is header-driven):', error)
    }
    localStorage.setItem(
      'selectedCustomerTenant',
      target.customerId ? `customer-${target.customerId}` : `tenant-${target.tenantId}`,
    )
    dispatch(
      target.customerId
        ? fetchLoggedInUserThunk({ customerId: target.customerId })
        : fetchLoggedInUserThunk({ tenantId: target.tenantId }),
    )
  }

  const accountOwner = formatMessage(messages.accountOwner)

  // E8 8.5.3 / WI 577 — a11y: announce the CURRENT persona, not a static label, so a screen reader reads
  // "Switch persona, currently TenantA — North". (Ctrl/Cmd-K was dropped per Q-E8-4; MUI Select already
  // gives combobox/listbox roles + aria-selected on the current option natively.)
  const current = personas.find((p) => personaValue(p) === selected)
  const switcherAriaLabel = current
    ? formatMessage(messages.switcherAriaLabel, { persona: personaLabel(current, accountOwner) })
    : formatMessage(messages.switcherLabel)

  return (
    <FormControl variant='standard' style={{ minWidth: 200 }}>
      <Select
        labelId='persona-switcher-label'
        value={selected}
        onChange={handleChange}
        style={{ borderBottom: 'none' }}
        inputProps={{ 'aria-label': switcherAriaLabel }}
      >
        {here.length > 0 && <ListSubheader>{formatMessage(messages.thisWorkspace)}</ListSubheader>}
        {here.map((p) => (
          <MenuItem key={personaValue(p)} value={personaValue(p)}>
            {personaLabel(p, accountOwner)}
          </MenuItem>
        ))}
        {elsewhere.length > 0 && (
          <ListSubheader>{formatMessage(messages.otherWorkspaces)}</ListSubheader>
        )}
        {elsewhere.map((p) => (
          <MenuItem key={personaValue(p)} value={personaValue(p)}>
            {personaLabel(p, accountOwner)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default PersonaSwitcher
